import type { ZodRawShape, ZodType } from "zod";

import type { ReadonlyRecord } from "./shared.ts";

export type FieldDescriptions<S extends ZodRawShape> = {
  readonly [K in keyof S]: string;
};

export type FieldDescriptionsArgs<S extends ZodRawShape> = keyof S extends never
  ? readonly []
  : readonly [descriptions: FieldDescriptions<S>];

export const FIELD_DESCRIPTIONS = new WeakMap<
  object,
  ReadonlyRecord<string, string>
>();

export function describeShape<S extends ZodRawShape>(
  shape: S,
  descriptions: ReadonlyRecord<string, string>,
): S {
  const described = {} as const;

  for (const [key, descriptor] of Object.entries(
    Object.getOwnPropertyDescriptors(shape),
  )) {
    const description = descriptions[key];

    Object.defineProperty(
      described,
      key,
      typeof description === "string"
        ? describeProperty(shape, key, descriptor, description)
        : descriptor,
    );
  }

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return described as S;
}

function describeProperty(
  shape: object,
  key: string,
  descriptor: PropertyDescriptor,
  description: string,
): PropertyDescriptor {
  if (!Object.hasOwn(descriptor, "get")) {
    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    const field = descriptor.value as ZodType;

    // oxlint-disable-next-line typescript/no-deprecated
    return { ...descriptor, value: field.describe(description) };
  }

  let describedField: ZodType | undefined;

  return {
    configurable: true,
    enumerable: true,
    get(): ZodType {
      // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
      const field = Reflect.get(shape, key) as ZodType;
      // oxlint-disable-next-line typescript/no-deprecated
      describedField ??= field.describe(description);

      return describedField;
    },
  };
}
